<script lang="ts">
  /**
   * The top-of-site notebook list (STEP_7_SPEC.md D7).
   *
   * A shelf of closed notebooks, three to a row, each one a square: spine down
   * the left, elastic band across the cover, name debossed on the front and the
   * `@tag` at the foot on the same left edge. The tag is the address captures
   * route by, so it is the one thing on the cover worth reading — which is why
   * it took the slot the op count used to hold, and why the count is gone. A
   * person choosing a notebook is not choosing one by how many operations are in
   * it.
   *
   * `New notebook` is a square of the same size and the same grid, so adding one
   * never shuffles the ones already there. It sits LAST for that reason: a new
   * notebook lands after the existing ones instead of pushing them along.
   *
   * Authors nothing: every callback belongs to the container, which owns the
   * stores and the modal.
   */
  import Plus from 'lucide/dist/esm/icons/plus.mjs';
  import Pencil from 'lucide/dist/esm/icons/pencil.mjs';
  import Settings from 'lucide/dist/esm/icons/settings.mjs';
  import type { NotebookSummary } from '../storage/operations';
  import Icon from './Icon.svelte';

  interface Props {
    notebooks: NotebookSummary[];
    onOpen: (notebookId: string) => void;
    onCreate: () => void;
    onEdit: (notebookId: string) => void;
    /** Opens the settings page (R3.2). Optional so the shelf renders standalone. */
    onSettings?: () => void;
    /**
     * The sign-in entry, beside Settings: an unclaimed host with sign-in
     * available offers the claim right where the header is, not adrift below
     * the shelf. Optional — absent when sign-in is not offered (D2).
     */
    onSignIn?: () => void;
  }

  let { notebooks, onOpen, onCreate, onEdit, onSettings, onSignIn }: Props = $props();
</script>

<div class="notebook-list" data-testid="notebook-list">
  <header class="notebook-list-header">
    <h1 tabindex="-1">Notebooks</h1>
    <!-- The settings page (import included) is button-reached only — no
         hotkey, the keyboard layer is dead on this view (Step 7). -->
    <button class="action" type="button" data-testid="notebook-settings" onclick={() => onSettings?.()}>
      <Icon node={Settings} size={14} />
      <span>Settings</span>
    </button>
    <!-- The claim entry, beside Settings (was adrift below the shelf, where
         nobody noticed it). Rendered only when sign-in is offered (D2). -->
    {#if onSignIn}
      <button class="action" type="button" data-testid="signin-entry" onclick={onSignIn}>
        [ Sign in ]
      </button>
    {/if}
  </header>

  <ul class="book-grid">
    {#each notebooks as notebook (notebook.notebook_id)}
      <li class="book" data-testid="notebook-list-item">
        <span class="book-spine" aria-hidden="true"></span>
        <span class="book-band" aria-hidden="true"></span>
        <button
          class="book-open"
          type="button"
          data-testid="notebook-list-open"
          onclick={() => onOpen(notebook.notebook_id)}
        >
          <span class="book-name">{notebook.name}</span>
          {#if notebook.description}
            <span class="book-description">{notebook.description}</span>
          {/if}
          <span class="book-tag">@{notebook.tag}</span>
        </button>
        <button
          class="book-edit"
          type="button"
          data-testid="notebook-list-edit"
          title="Edit {notebook.name}"
          onclick={() => onEdit(notebook.notebook_id)}
        >
          <Icon node={Pencil} size={13} />
          <span class="visually-hidden">Edit {notebook.name}</span>
        </button>
      </li>
    {/each}

    <!-- Last, so a new notebook appends rather than displaces. -->
    <li class="book book--new">
      <button
        class="book-open book-open--new"
        type="button"
        data-testid="notebook-list-create"
        onclick={onCreate}
      >
        <Icon node={Plus} size={18} />
        <span>New notebook</span>
      </button>
    </li>
  </ul>

  {#if notebooks.length === 0}
    <p class="notebook-list-empty">No notebooks yet.</p>
  {/if}
</div>

<style>
  .notebook-list {
    /* Same page as the notebook: the list is the front door, not an app chrome
       screen. Widths follow the stage's rhythm. */
    width: min(100%, 60em);
    margin: 0 auto;
    padding: 2rem 1rem;
  }

  .notebook-list-header {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 1rem;
  }

  h1 {
    font-family: inherit;
    font-size: 1.4rem;
    font-weight: normal;
  }

  .notebook-list-empty {
    color: var(--ink-muted);
  }

  .visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
  }

  /* ── the shelf ─────────────────────────────────────────────────────────────
     Three across, then two, then one. A fixed `repeat(3, 1fr)` rather than
     `auto-fill`: the shelf is three books wide because that is the shape of it,
     not because that is how much room happens to be free — `auto-fill` fits a
     fourth column inside the 60em page and turns the grid into something else
     on a wide window.

     Each breakpoint also caps the grid's own width. A square tile grows with its
     column, so an uncapped 2-up or 1-up turns a book cover into a 350px void
     with three words on it; capping keeps every tile the same book-sized square
     at every width, which is also what makes `New notebook` reliably the same
     size as the books beside it. */
  ul {
    list-style: none;
    margin: 1.25rem auto 0;
    padding: 0;
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 20px;
    max-width: 928px;
  }

  @media (max-width: 860px) {
    ul {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      max-width: 640px;
    }
  }

  @media (max-width: 520px) {
    ul {
      grid-template-columns: minmax(0, 1fr);
      max-width: 340px;
    }
  }

  /* A square. `aspect-ratio` rather than a height, so the tile IS square at
     any column width instead of being square only at the one size it was
     designed at. */
  .book {
    position: relative;
    aspect-ratio: 1;
    background: color-mix(in srgb, var(--accent) 9%, var(--paper));
    border-radius: 2px 3px 3px 2px;
    box-shadow: var(--shadow-card);
    overflow: hidden;
  }

  .book-spine {
    position: absolute;
    inset: 0 auto 0 0;
    width: 17px;
    background: color-mix(in srgb, var(--accent) 62%, #000);
  }

  /* The stitching along the spine. A repeating gradient rather than an asset:
     it is two horizontal dashes at a fixed pitch. */
  .book-spine::after {
    content: '';
    position: absolute;
    inset: 9px 5px;
    background-image: linear-gradient(
      to bottom,
      color-mix(in srgb, var(--paper) 65%, transparent) 0 3px,
      transparent 3px 14px
    );
    background-size: 3px 14px;
  }

  .book-band {
    position: absolute;
    top: -6%;
    bottom: -6%;
    right: 22px;
    width: 5px;
    background: color-mix(in srgb, var(--ink) 16%, transparent);
  }

  /* The whole cover opens the notebook. It is a sibling of the pencil, never a
     parent of it: a button inside a button is invalid HTML and the click would
     land on whichever one the browser picked. */
  .book-open {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    /* `stretch`, not `flex-start`: a shrink-to-fit flex item takes its
       max-content width, so a long description ran straight past the elastic
       band and off the cover instead of wrapping at the writing column. */
    align-items: stretch;
    gap: 7px;
    /* Clear of the spine and the band — the cover's writing column. */
    padding: 15px 30px 10px 28px;
    border: none;
    background: none;
    font: inherit;
    color: inherit;
    text-align: left;
    cursor: pointer;
  }

  .book-open:hover {
    background: color-mix(in srgb, var(--accent) 5%, transparent);
  }

  .book-open:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: -2px;
  }

  .book-name {
    font-size: var(--hand-size);
    line-height: 1.15;
  }

  .book-description {
    font-family: var(--font-ui);
    font-size: 11px;
    line-height: 1.35;
    color: var(--ink-muted);
    /* Clamped so a long description can never grow into the tag at the foot. */
    display: -webkit-box;
    line-clamp: 3;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  /* The address, on the name's left edge. `margin-top: auto` pins it to the
     foot of whatever height the tile is, so the tag sits in the same place on
     every book regardless of how long the name ran. */
  .book-tag {
    margin-top: auto;
    font-family: var(--font-mono, monospace);
    font-size: 10px;
    color: var(--accent);
  }

  /* The pencil: faint at rest, forward on hover AND on focus. Always present in
     the DOM — a hover-only affordance is unreachable by keyboard, which is the
     one way this list must not be edited from. */
  .book-edit {
    position: absolute;
    top: 8px;
    right: 9px;
    width: 24px;
    height: 24px;
    display: grid;
    place-items: center;
    border: none;
    border-radius: 2px;
    background: none;
    color: var(--ink-muted);
    opacity: 0.5;
    cursor: pointer;
    transition: opacity 120ms ease;
  }

  .book:hover .book-edit,
  .book-edit:focus-visible {
    opacity: 1;
  }

  .book-edit:hover {
    background: color-mix(in srgb, var(--ink) 8%, transparent);
  }

  .book-edit:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 1px;
  }

  /* Same square, same cell — visibly not a book, because it is the one thing on
     this page that is not a notebook yet. */
  .book--new {
    background: color-mix(in srgb, var(--paper) 35%, transparent);
    border: 1.5px dashed color-mix(in srgb, var(--ink-muted) 75%, transparent);
    border-radius: 3px;
    box-shadow: none;
  }

  .book-open--new {
    align-items: center;
    justify-content: center;
    gap: 8px;
    color: var(--ink-muted);
    font-family: var(--font-ui);
    font-size: 11px;
    letter-spacing: 0.04em;
    text-align: center;
  }

  .book-open--new:hover {
    background: color-mix(in srgb, var(--paper) 60%, transparent);
  }
</style>